/**
 * Platform-admin constants shared by the operator console (`apps/admin`), the
 * cross-tenant API in `apps/saas-api`, and the customer app (`apps/web`), which
 * needs the role names to avoid rendering operator affordances to customers.
 *
 * Nothing here imports Zod or Prisma on purpose: these are the wire-level
 * vocabulary both sides must agree on, and they have to be readable from a
 * Better Auth access-control definition without pulling a validation layer in.
 */

// ============ Wire constants ============

/**
 * Prefix for every cross-tenant admin route. Kept here rather than in the
 * client so the route definitions and the Eden path agree by construction.
 */
export const ADMIN_API_PREFIX = "/v1/admin";

/**
 * Base path of the *second* Better Auth instance. The customer instance stays
 * on `/auth`; splitting the path is what lets the two issue independent
 * sessions over one set of tables.
 */
export const ADMIN_AUTH_BASE_PATH = "/admin/auth";

/**
 * Cookie prefix for the admin instance. Distinct from the customer instance's
 * default so the two cookies cannot collide, and paired with
 * `crossSubDomainCookies: false` so the admin cookie stays host-only and is
 * unreadable from `veilbrowser.net`.
 */
export const ADMIN_AUTH_COOKIE_PREFIX = "veil-admin";

// ============ Platform roles ============

/**
 * Staff roles. Distinct from the org-scoped `owner | admin | member` on
 * `Member`: an org `admin` manages one tenant, a platform role manages
 * VeilBrowser. `defaultRole` on the admin plugin stays `user`, so a customer
 * account carries none of these.
 */
export const PLATFORM_ROLES = ["superadmin", "support", "billing"] as const;

export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export const PLATFORM_ROLE_LABELS: Record<PlatformRole, string> = {
	superadmin: "Super admin",
	support: "Support",
	billing: "Billing",
};

export const PLATFORM_ROLE_DESCRIPTIONS: Record<PlatformRole, string> = {
	superadmin:
		"Full access, including promoting other operators and deleting accounts.",
	support:
		"Read accounts, reset passwords and revoke sessions. Cannot ban, delete, or touch billing.",
	billing:
		"Manage plans and subscription overrides, and read accounts. Nothing else.",
};

export function isPlatformRole(value: unknown): value is PlatformRole {
	return (
		typeof value === "string" &&
		(PLATFORM_ROLES as readonly string[]).includes(value)
	);
}

// ============ Permissions ============

/**
 * The access-control statement: which actions exist on which resource. This
 * mirrors `platformStatement` in `apps/saas-api/src/lib/platform-access.ts`,
 * which is what `createAccessControl()` is actually handed, so the panel can
 * decide whether to render an affordance using the same vocabulary the guard
 * behind it enforces.
 *
 * `user` and `session` are the admin plugin's `defaultStatements` verbatim,
 * restated rather than imported because this package must not depend on
 * `better-auth` — `apps/web` reads these constants too and has no reason to
 * pull an auth library into its bundle for a list of strings. That restatement
 * is the cost of the independence and has to be kept in step by hand.
 *
 * Adding a resource means adding it in both places in the same change. A
 * resource the panel knows about and the server does not is worse than no
 * resource at all: it renders a button whose request is refused.
 */
export const PLATFORM_STATEMENTS = {
	user: [
		"create",
		"list",
		"set-role",
		"ban",
		"impersonate",
		"impersonate-admins",
		"delete",
		"set-password",
		// Added by the admin plugin in better-auth 1.6.26. Present in the
		// vocabulary because the server's statement spreads `defaultStatements`
		// and the parity test compares the two, but granted to no role: no route
		// changes a customer's email, and an operator who could would be able to
		// take over an account in two steps.
		"set-email",
		"get",
		"update",
	],
	session: ["list", "revoke", "delete"],
	/**
	 * Tenants and everything a tenant owns.
	 *
	 * One resource rather than five, because `profile`, `profileSession`,
	 * `licenseSlot` and `apikey` are all "data belonging to some organization"
	 * and no role would ever be granted one of them without the others. The
	 * actions are what differ by role, so the granularity lives there:
	 *
	 * - `list` / `get` — cross-tenant reads, including profiles, sessions, API
	 *   keys, usage and launch diagnostics.
	 * - `update` / `delete` — edit or remove the organization itself.
	 * - `manage-members` / `transfer-ownership` — repair a broken membership.
	 * - `manage-sessions` — kill a stuck `ProfileSession`, clear a `LicenseSlot`,
	 *   revoke an API key. Recoverable operational actions: the customer relaunches
	 *   or mints a new key, so this is the one write support gets.
	 * - `manage-resources` — force-delete a customer's profile. Destructive and
	 *   not something the customer can undo, so it stays an escalation.
	 */
	organization: [
		"list",
		"get",
		"update",
		"delete",
		"manage-members",
		"transfer-ownership",
		"manage-sessions",
		"manage-resources",
	],
	/**
	 * Reading `AdminAuditLog`. Separate from `setting` because "who did what"
	 * is a different question from "change how the platform behaves", and the
	 * role that answers support questions needs the former without the latter.
	 * Write is absent by construction: nothing may edit or delete an audit row.
	 */
	audit: ["read"],
	plan: ["create", "update", "delete"],
	subscription: ["read", "override"],
	setting: ["read", "update"],
} as const satisfies Record<string, readonly string[]>;

export type PlatformResource = keyof typeof PLATFORM_STATEMENTS;

export type PlatformAction<R extends PlatformResource> =
	(typeof PLATFORM_STATEMENTS)[R][number];

type PlatformPermissions = {
	[R in PlatformResource]?: readonly PlatformAction<R>[];
};

/**
 * What each role may do, matching `platformRoles` in saas-api.
 *
 * `impersonate` and `impersonate-admins` are absent from every role, which is
 * the whole point of the product decision behind them: the console does
 * read-only view-as, so no operator can mint a customer session even though the
 * plugin is capable of it.
 *
 * Support gets identity reads plus a password reset, which is the content of
 * most support requests. Banning, deletion and role changes are escalations
 * because they are irreversible or privilege-granting. Billing gets the plan
 * and subscription writes and nothing else.
 *
 * On the tenant side, support reads every organization and may kill a stuck
 * session, clear a launch reservation or revoke an API key — the actions a
 * customer can recover from on their own. Editing or deleting an organization,
 * repairing memberships and force-deleting a profile are superadmin only.
 * Billing reads organizations because a subscription override needs the tenant
 * in front of it, and holds no tenant write at all.
 *
 * `audit` goes to superadmin and support: the log is how "who changed this"
 * gets answered, which is a support question. Billing is left out because the
 * log is mostly identity actions — bans, password resets, role changes — and a
 * plan operator has no reason to read those.
 *
 * Absent from a role's map means denied, so a resource a role has no entry for
 * (settings, for both support and billing) is simply refused.
 */
export const PLATFORM_ROLE_PERMISSIONS: Record<
	PlatformRole,
	PlatformPermissions
> = {
	superadmin: {
		user: [
			"create",
			"list",
			"get",
			"update",
			"set-role",
			"set-password",
			"ban",
			"delete",
		],
		session: ["list", "revoke", "delete"],
		organization: [
			"list",
			"get",
			"update",
			"delete",
			"manage-members",
			"transfer-ownership",
			"manage-sessions",
			"manage-resources",
		],
		audit: ["read"],
		plan: ["create", "update", "delete"],
		subscription: ["read", "override"],
		setting: ["read", "update"],
	},
	support: {
		user: ["list", "get", "set-password"],
		session: ["list", "revoke"],
		organization: ["list", "get", "manage-sessions"],
		audit: ["read"],
	},
	billing: {
		user: ["list", "get"],
		organization: ["list", "get"],
		plan: ["create", "update"],
		subscription: ["read", "override"],
	},
};

/**
 * Does `role` permit `action` on `resource`? Used by the panel to omit an
 * affordance rather than disable it — a disabled button is a bug waiting to be
 * re-enabled — and never as the only check, since the route guard is the one
 * that matters.
 */
export function platformRoleCan<R extends PlatformResource>(
	role: string | null | undefined,
	resource: R,
	action: PlatformAction<R>,
): boolean {
	if (!isPlatformRole(role)) return false;
	const allowed = PLATFORM_ROLE_PERMISSIONS[role][resource];
	return allowed ? (allowed as readonly string[]).includes(action) : false;
}

// ============ Audit actions ============

/**
 * Every action name that may appear in `AdminAuditLog.action`. Keeping it a
 * closed union means the audit-log filter in the panel is generated from the
 * same list the writer uses, so a renamed action fails a typecheck rather than
 * silently disappearing from a filter dropdown.
 */
export const ADMIN_AUDIT_ACTIONS = [
	// Identity
	"user.create",
	"user.update",
	"user.set_role",
	"user.set_password",
	"user.ban",
	"user.unban",
	"user.delete",
	"user.sessions_revoked",
	// Tenants
	"organization.update",
	"organization.delete",
	"organization.member_added",
	"organization.member_removed",
	"organization.member_role_changed",
	"organization.owner_transferred",
	// Billing
	"plan.create",
	"plan.update",
	"plan.delete",
	"subscription.override",
	"subscription.cancel",
	"subscription.reconcile",
	// Operations
	"profile.delete",
	"profile_session.kill",
	"license_slot.clear",
	"apikey.revoke",
	// Platform
	"setting.update",
	"view_as.start",
	"activity_partition.drop",
] as const;

export type AdminAuditAction = (typeof ADMIN_AUDIT_ACTIONS)[number];

/**
 * Target types an audit entry can point at. `AdminAuditLog.targetType` is a
 * free string in the schema; this is the set the routes are expected to write.
 */
export const ADMIN_AUDIT_TARGET_TYPES = [
	"user",
	"session",
	"organization",
	"member",
	"plan",
	"subscription",
	"profile",
	"profileSession",
	"licenseSlot",
	"apikey",
	"setting",
	"activityPartition",
] as const;

export type AdminAuditTargetType = (typeof ADMIN_AUDIT_TARGET_TYPES)[number];

// ============ Tenant activity actions ============

/**
 * `ActivityEvent.action` values. Session open/close deliberately absent:
 * `ProfileSession` already records those permanently and the feed unions the
 * two, so each fact keeps exactly one writer.
 */
export const ACTIVITY_EVENT_ACTIONS = [
	"auth.signup",
	"auth.signin",
	"auth.signin_failed",
	"auth.signout",
	"auth.password_reset_requested",
	"auth.password_reset_completed",
	"auth.oauth_linked",
	"auth.session_revoked",
	"member.invited",
	"member.removed",
	"member.role_changed",
	"invitation.cancelled",
	"apikey.created",
	"apikey.revoked",
	"org.updated",
	"org.deleted",
	"plan.changed",
	"profile.created",
	"profile.deleted",
	"profile.stop_requested",
] as const;

export type ActivityEventAction = (typeof ACTIVITY_EVENT_ACTIONS)[number];

export const ACTIVITY_ACTOR_TYPES = ["user", "apiKey", "system"] as const;

export type ActivityActorType = (typeof ACTIVITY_ACTOR_TYPES)[number];
